/**
 * A table column header you can click to sort by.
 *
 * Thin on purpose: it is `TableCell` plus `TableSortLabel` plus the one thing
 * both of them need and neither does on its own — `aria-sort` on the cell, so a
 * screen reader announces which column the table is ordered by rather than
 * leaving it as a visual-only cue.
 *
 * It renders nothing about *where* the data came from. The page owns the state
 * (see `hooks/useListQuery`) and the request.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import TableCell, { type TableCellProps } from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import type { SortOrder } from '@forsys/shared';

export interface SortableHeadCellProps extends Omit<TableCellProps, 'onClick' | 'sortDirection'> {
  /** The API's field name, not the column's label. `displayName`, not "Name". */
  field: string;
  /** The field the table is currently sorted by — from the page's list state. */
  activeField: string;
  activeOrder: SortOrder;
  onSort: (field: string) => void;
  children: ReactNode;
}

export function SortableHeadCell(props: SortableHeadCellProps) {
  const { field, activeField, activeOrder, onSort, children, ...cell } = props;
  const { t } = useTranslation('common');

  const active = activeField === field;

  return (
    <TableCell
      {...cell}
      sortDirection={active ? activeOrder : false}
      // `aria-sort` belongs on the header cell, not on the button inside it.
      // Putting it on the label leaves the column itself unlabelled to assistive
      // technology, which is most of the point of a sortable header.
      aria-sort={active ? (activeOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <TableSortLabel
        active={active}
        direction={active ? activeOrder : 'asc'}
        onClick={() => onSort(field)}
      >
        {children}
        {/* Announced only while this column is the sorted one — an inactive
            header would otherwise read out a direction it is not in. */}
        {active && (
          <span
            style={{
              border: 0,
              clip: 'rect(0 0 0 0)',
              height: 1,
              margin: -1,
              overflow: 'hidden',
              padding: 0,
              position: 'absolute',
              width: 1,
            }}
          >
            {activeOrder === 'desc' ? t('sort.descending') : t('sort.ascending')}
          </span>
        )}
      </TableSortLabel>
    </TableCell>
  );
}
